import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import { getAllBeds, deleteBed } from "../../services/BedService";
import { confirmAsync } from "../../utils/appNotify";
import AddBedModal from "./Addbeds.jsx";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import { RemoveRedEye } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { getOrganizationName } from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import dayjs from "dayjs";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";

const columns = [
  { id: "id", label: "ID" },
  { id: "wardName", label: "Ward Name" },
  { id: "bedNumber", label: "Bed Number" },
  { id: "bedPrice", label: "Bed Price" },
  { id: "isOccupied", label: "Is Occupied" },
  { id: "bedType", label: "Bed Type" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

function BedsTable() {
  const [page, setPage] = useState(0);
  const theme = useTheme();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ beds_list: true });
  const [bedsList, setBedsList] = useState([]);
  const [openBedModal, setOpenBedModal] = useState(false);
  const [bedInfo, setBedInfo] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const roleName = (user?.roleName || "").toLowerCase();
  const canDelete = roleName === "admin" || roleName === "super_admin" || roleName === "superadmin";
  const [searchQuery, setSearchQuery] = useState("");
  const [filterWard, setFilterWard] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterOccupied, setFilterOccupied] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchBedsList();
  }, [location.key]);

  const fetchBedsList = async () => {
    setFetching({ ...fetching, beds_list: true });
    try {
      const data = await getAllBeds();
      // Ensure data is an array
      const dataArray = Array.isArray(data) ? data : [];
      const sorted = [...dataArray].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setBedsList(sorted);
    } catch (error) {
      console.error("Failed to fetch beds:", error);
      // Set empty array on error to show "No records found" message
      setBedsList([]);
    } finally {
      setFetching({ ...fetching, beds_list: false });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const isBedOccupied = (value) =>
    value === true || value === "true" || value === 1 || value === "1";

  const handleDeleteBed = async (bed) => {
    if (isBedOccupied(bed?.isOccupied)) {
      window.alert(
        `Cannot delete bed "${bed.bedNumber || bed.id}". This bed is currently occupied.`
      );
      return;
    }

    const confirmed = await confirmAsync(
      `Are you sure you want to delete bed "${bed.bedNumber || bed.id}" in ward "${bed.wardName || "N/A"}"?`,
      { title: "Delete bed", tone: "error", confirmLabel: "Delete" }
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteBed(bed.id);
      await fetchBedsList();
    } catch (error) {
      console.error("Failed to delete bed:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete bed. Please try again.";
      window.alert(message);
    }
  };

  const filteredRows = bedsList.filter((b) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [b.id, b.wardName, b.bedNumber, b.bedPrice, b.bedType, b.isOccupied ? "Yes" : "No"]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (filterWard) {
      if (String(b.wardName || "") !== filterWard) return false;
    }
    if (filterType) {
      if (String(b.bedType || "") !== filterType) return false;
    }
    if (filterOccupied) {
      const occ = b.isOccupied ? "Yes" : "No";
      if (occ !== filterOccupied) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterWard("");
    setFilterType("");
    setFilterOccupied("");
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["ID", "Ward Name", "Bed Number", "Bed Price", "Is Occupied", "Bed Type"];
    const tableRows = rows.map((r) => [
      r.id,
      r.wardName,
      r.bedNumber,
      r.bedPrice,
      r.isOccupied ? "Yes" : "No",
      r.bedType,
    ]);

    await downloadStyledTableExcel({
      filename: `beds_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Beds",
      title: "Beds",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={() => {
            setOpenBedModal(true);
          }}
        >
          Add Bed
        </Button>
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={fetchBedsList}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField size="small" label="Ward" value={filterWard} onChange={(e)=>setFilterWard(e.target.value)} select sx={{ minWidth: 180 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(bedsList.map((b)=>b.wardName).filter(Boolean))].map((w)=> (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Bed Type" value={filterType} onChange={(e)=>setFilterType(e.target.value)} select sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {[...new Set(bedsList.map((b)=>b.bedType).filter(Boolean))].map((t)=> (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Occupied" value={filterOccupied} onChange={(e)=>setFilterOccupied(e.target.value)} select sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ minWidth: 100 }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching.beds_list ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : bedsList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Alert severity="info">No beds found</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((bed) => (
                    <TableRow key={bed.id} hover role="checkbox" tabIndex={-1}>
                      {columns.map((column) => {
                        let value = bed[column.id];
                        if (column.id === "isOccupied") {
                          value = value ? "Yes" : "No";
                        }
                        return (
                          <TableCell key={column.id}>
                            {value}
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <Box display={"flex"} gap={1}>
                          <IconButton
                            size="small"
                            sx={TABLE_ICON_BUTTON_SX}
                            onClick={() => {
                              setOpenBedModal(true);
                              setBedInfo(bed);
                            }}
                          >
                            <ModeEditOutlineTwoToneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={TABLE_ICON_BUTTON_SX}
                            onClick={() => {
                              setOpenBedModal(true);
                              setBedInfo({ ...bed, readOnly: true });
                            }}
                          >
                            <RemoveRedEye fontSize="small" />
                          </IconButton>
                          {canDelete && (
                            <Tooltip title="Delete Bed">
                              <IconButton
                                sx={{ color: theme.palette.text.secondary }}
                                onClick={() => handleDeleteBed(bed)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <AddBedModal
        open={openBedModal}
        onClose={async () => {
          setOpenBedModal(false);
          setBedInfo(null);
          await fetchBedsList();
        }}
        bedInfo={bedInfo}
        readOnly={!!bedInfo?.readOnly}
      />
    </Grid>
  );
}

export default BedsTable;
